import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import TrendingFlatRounded from "@mui/icons-material/TrendingFlatRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useEffect, useState, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { AI_INSIGHTS, CATEGORY_COUNTS, CHART_COLORS, STATUS_DISTRIBUTION } from "../data";
import { getInsights } from "../../../../services/timeline.service";
import ChartShell from "./ChartShell";

const PIE_COLORS = {
  Normal: CHART_COLORS.success,
  Attention: CHART_COLORS.warning,
  Critical: CHART_COLORS.danger,
  Pending: CHART_COLORS.primary
};

const tooltipStyle = {
  contentStyle: {
    borderRadius: 12,
    border: "1px solid rgb(226 232 240)",
    boxShadow: "0 4px 12px rgba(15,23,42,0.08)"
  },
  labelStyle: { fontWeight: 600, color: "#0f172a" }
};

/* ─── Status config ──────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  positive: {
    Icon: CheckCircleRounded,
    border: "border-emerald-200 bg-emerald-50/40",
    iconBg: "bg-emerald-100 text-emerald-600",
    pill: "bg-emerald-100 text-emerald-700"
  },
  warning: {
    Icon: WarningAmberRounded,
    border: "border-amber-200 bg-amber-50/40",
    iconBg: "bg-amber-100 text-amber-600",
    pill: "bg-amber-100 text-amber-700"
  },
  critical: {
    Icon: ErrorOutlineRounded,
    border: "border-red-200 bg-red-50/40",
    iconBg: "bg-red-100 text-red-600",
    pill: "bg-red-100 text-red-700"
  },
  stable: {
    Icon: TrendingFlatRounded,
    border: "border-sky-200 bg-sky-50/40",
    iconBg: "bg-sky-100 text-sky-600",
    pill: "bg-sky-100 text-sky-700"
  }
};

function InsightCard({ insight }) {
  const cfg = STATUS_CONFIG[insight.status] || STATUS_CONFIG.stable;
  const { Icon } = cfg;
  const points = insight.data_points || [];

  return (
    <article
      className={`group rounded-2xl border p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${cfg.border}`}
    >
      {/* Top row: icon + title */}
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${cfg.iconBg}`}
        >
          <Icon sx={{ fontSize: 18 }} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900 leading-snug">
            {insight.title}
          </h3>
        </div>
      </div>

      {/* Description */}
      <p className="mt-2.5 text-[13px] leading-relaxed text-slate-600">
        {insight.description}
      </p>

      {/* Data points as pills */}
      {points.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {points.map((dp, i) => (
            <span
              key={i}
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cfg.pill}`}
            >
              {dp}
            </span>
          ))}
        </div>
      )}

      {/* Action */}
      {insight.action && (
        <p className="mt-3 border-t border-slate-200/60 pt-2.5 text-[12px] font-medium text-slate-500 leading-snug">
          → {insight.action}
        </p>
      )}
    </article>
  );
}

/* ─── Skeleton card for loading state ────────────────────────────────── */
function InsightSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-slate-200/60 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-slate-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 rounded bg-slate-200" />
          <div className="h-3 w-full rounded bg-slate-100" />
          <div className="h-3 w-5/6 rounded bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

export default function AIInsights({ reports }) {
  const [liveCards, setLiveCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const data = await getInsights();
        // Look for the 'derived' bundle which has our insight cards
        const derived = Array.isArray(data)
          ? data.find(item => item.isDerived)
          : null;
        if (derived && derived.cards && derived.cards.length > 0) {
          setLiveCards(derived.cards);
        }
      } catch (e) {
        console.error("Failed to fetch live insight cards", e);
        setError("Could not load insights");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Use live data if available, otherwise static fallback
  const insightCards = useMemo(() => {
    return liveCards || AI_INSIGHTS;
  }, [liveCards]);

  const pieData = useMemo(() => {
    if (!reports || !reports.length) return STATUS_DISTRIBUTION.filter((d) => d.value > 0);
    const counts = { Normal: 0, Attention: 0, Critical: 0, Pending: 0 };
    reports.forEach((r) => {
      let st = r.status || "Normal";
      const lower = st.toLowerCase();
      if (lower === "stable") st = "Normal";
      else if (lower === "warning") st = "Attention";
      else st = st.charAt(0).toUpperCase() + st.slice(1).toLowerCase();
      
      if (counts[st] !== undefined) counts[st]++;
      else counts["Normal"]++;
    });
    return Object.keys(counts)
      .map(name => ({ name, value: counts[name] }))
      .filter((d) => d.value > 0);
  }, [reports]);

  const barData = useMemo(() => {
    if (!reports || !reports.length) return CATEGORY_COUNTS;
    const catMap = {};
    reports.forEach((r) => {
      const c = r.category || "Other";
      catMap[c] = (catMap[c] || 0) + 1;
    });
    return Object.keys(catMap)
      .map(category => ({ category, count: catMap[category] }))
      .sort((a, b) => b.count - a.count);
  }, [reports]);

  return (
    <div className="min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-white px-4 py-3 shadow-sm">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-600 text-white shadow-md shadow-violet-600/25">
          <AutoAwesomeRounded sx={{ fontSize: 22 }} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">AI-powered insights</p>
          <p className="text-xs text-slate-500">
            Pattern detection across your uploaded reports — not a substitute for clinical advice.
          </p>
        </div>
      </div>

      {/* Insight cards grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map(k => <InsightSkeleton key={k} />)}
        </div>
      ) : error && !liveCards ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 text-sm text-amber-800">
          {error} — showing cached insights.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {insightCards.map((insight) => (
          <InsightCard key={insight.title} insight={insight} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartShell title="Report status mix" subtitle="Share of results by severity bucket.">
          <div className="h-64 w-full min-h-[220px] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <PieChart>
                <Tooltip {...tooltipStyle} />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="48%"
                  innerRadius={56}
                  outerRadius={88}
                  paddingAngle={2}
                >
                  {pieData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={PIE_COLORS[entry.name] || CHART_COLORS.secondary}
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartShell>

        <ChartShell title="Tests by category" subtitle="Volume of completed tests across specialties.">
          <div className="h-64 w-full min-h-[220px] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="category"
                  width={100}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Tests" fill={CHART_COLORS.primary} radius={[0, 8, 8, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartShell>
      </div>
    </div>
  );
}

